import React, { useState } from 'react';
import { Button, Space, Tag, Tooltip, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { userPath } from '../config/paths';
import { formatDate } from '../utils/date';
import { useTranslation } from '../i18n';

/**
 * 续费 = 加入购物车，和其他商品统一结算；已有未付续费单则直接去付款。
 * 返回 true 表示已加入/已跳转。
 */
export async function addRenewalToCart({ subscriptionId, resourceName, navigate, t }) {
    try {
        const res = await apiClient.post('/api/user/cart/add-renewal', { subscription_id: subscriptionId, resource_name: resourceName });
        const d = res.data || {};
        const data = d.data || {};
        if (!d.success) {
            message.error(d.error || d.message || t('Failed to add to cart'));
            return false;
        }
        if (data.invoice_id) {
            navigate(userPath(`/billing/invoices/${data.invoice_id}`));
            return true;
        }
        window.dispatchEvent(new CustomEvent('cart:updated'));
        message.success({
            content: (
                <span>
                    {data.already_in_cart ? t('Already in cart') : t('Added to cart')}
                    <Button type="link" size="small" onClick={() => navigate(userPath('/billing/cart'))}>{t('Checkout')}</Button>
                </span>
            ),
            duration: 4,
        });
        return true;
    } catch (err) {
        message.error(err?.response?.data?.error || err?.message || t('Failed to add to cart'));
        return false;
    }
}

/**
 * 资源旁的到期状态 + 续费入口（2026-10-18）：用户不用去费用中心找。
 * billing = 后端 SubscriptionSummary：{ subscription_id, status, expiry_date, days_left, expired, auto_renew, renewable }
 * 已过期：红色「已过期」+ 续费；30 天内到期：橙色「N 天后到期」+ 续费；更远：只显示到期日（compact 时不显示）。
 */
const RenewAction = ({ billing, compact = false, size = 'small', onRenewed, resourceName }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    if (billing && billing.pending) {
        return (
            <Space size={6} wrap onClick={(e) => e.stopPropagation()}>
                <Tag color="processing" style={{ margin: 0 }}>{t('Payment pending')}</Tag>
                {billing.unpaid_invoice_id && (
                    <Button type="primary" size={size} onClick={() => navigate(userPath(`/billing/invoices/${billing.unpaid_invoice_id}`))}>{t('Pay now')}</Button>
                )}
            </Space>
        );
    }
    if (!billing || !billing.expiry_date) return null;

    const soon = !billing.expired && billing.days_left !== null && billing.days_left <= 30;
    if (!billing.expired && !soon && compact && !billing.pending_upgrade_invoice_id) return null;

    const renew = async (e) => {
        e?.stopPropagation?.();
        setBusy(true);
        try {
            const ok = await addRenewalToCart({ subscriptionId: billing.subscription_id, resourceName, navigate, t });
            if (ok && onRenewed) onRenewed();
        } finally {
            setBusy(false);
        }
    };

    let tag = null;
    if (billing.expired) {
        const next = billing.next;
        const label = billing.status === 'suspended' ? t('Paused') : t('Expired');
        const hint = next && (next.action === 'destroy'
            ? t('deleted in {{days}} days', { days: next.days })
            : t('suspended in {{days}} days', { days: next.days }));
        tag = (
            <Tooltip title={next ? t('Renew before {{date}} to keep it', { date: formatDate(next.at) }) : null}>
                <Tag color="error" style={{ margin: 0 }}>{hint ? `${label} · ${hint}` : label}</Tag>
            </Tooltip>
        );
    } else if (soon) {
        tag = <Tag color="warning" style={{ margin: 0 }}>{t('Expires in {{days}} days', { days: Math.max(0, billing.days_left) })}</Tag>;
    }

    return (
        <Space size={6} wrap onClick={(e) => e.stopPropagation()}>
            {billing.pending_upgrade_invoice_id && (
                <Button size={size} type="link" style={{ padding: 0 }} onClick={() => navigate(userPath(`/billing/invoices/${billing.pending_upgrade_invoice_id}`))}>
                    {t('Upgrade unpaid')}
                </Button>
            )}
            {tag || <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{t('Expires {{date}}', { date: formatDate(billing.expiry_date) })}</span>}
            {(billing.expired || soon) && billing.renewable && (
                <Tooltip title={billing.auto_renew && !billing.expired ? t('Auto-renew is on; you can also renew early') : null}>
                    <Button type={billing.expired ? 'primary' : 'default'} size={size} loading={busy} onClick={renew}>
                        {t('Renew')}
                    </Button>
                </Tooltip>
            )}
        </Space>
    );
};

export default RenewAction;
